import { Box, CircularProgress, Dialog } from '@material-ui/core';
import { CloseIcon1, GetCategoriesIcon, GetSportIcon, NoEventsFound, PopupCloseIcon, SearchIcon } from 'assets/icons';
import { t } from 'i18next';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { NavLink } from 'react-router-dom';
import { SearchEvents, setDataLayer } from 'shared/constants/datalayer';
import useSearchBar from 'shared/hooks/useSearchbar';
import { highlightSubstring } from 'shared/util/utility';
import AnimatedPlaceholder from '../AnimatedPlaceholderInput';

const SearcherDesktop: React.FC = () => {
	const { searchList, loading, searchValue, showSearch, setShowSearch, eventUrl, setSearchValue, closeSearchModal, handleSearchInput } =
		useSearchBar();

	const closeModal = () => {
		closeSearchModal();
		setDataLayer(SearchEvents.DATALAYER_SEARCH_CLOSE);
	};

	const clearInputField = () => {
		setSearchValue('');
		setDataLayer(SearchEvents.DATALAYER_SEARCH_CANCEL(searchValue));
	};

	const clickEventCard = () => {
		closeSearchModal();
		setDataLayer(SearchEvents.DATALAYER_SEARCH_SUCCESS);
	};

	const getIsCategoryLevel = (categoryLevel?: number) => {
		if (!categoryLevel) return false;
		if (categoryLevel === 1 || categoryLevel === 2) return true;
	};

	return (
		<div className="searcher_main_wrapper">
			<div onClick={() => setShowSearch(true)} className="desktop-searcher-trigger" data-cy="quick-search">
				<SearchIcon className="search-icon-trigger" />
				<AnimatedPlaceholder delayAfterAnimation={1000} className="desktop-animated-search" isInputFieldRequired={true} />
			</div>
			<Dialog className="searcher_modal_element" open={showSearch} onClose={closeSearchModal}>
				<Box>
					<div
						className={`side-nav side-nav-desktop position--relative ${showSearch ? 'side-nav-search-view' : ''}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="search__header-grid">
							<div className={`search__wrapper box-sizing--border-box`}>
								<SearchIcon className={'search-icon'} />
								<AnimatedPlaceholder
									delayAfterAnimation={1000}
									className="search-field"
									value={searchValue}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										e.stopPropagation();
										handleSearchInput(e);
									}}
									onFocus={(e: React.ChangeEvent<HTMLInputElement>) => {
										e.stopPropagation();
									}}
									data-cy="search-input"
									autoFocus
								/>
								{searchValue.length > 0 && (
									<span data-cy="clear-search-input">
										<CloseIcon1 className="clear-search pointer" onClick={clearInputField} />
									</span>
								)}
							</div>
							<PopupCloseIcon className="close--icon pointer" onClick={closeModal} />
						</div>
						{searchValue.length < 3 && searchValue.length > 0 && (
							<p className="error-too-short-string-searchbar" data-cy="search-error">
								{t('tooShortSearchValueInfo')}
							</p>
						)}
						<div className="side-nav__content flex flex--column justify__content--between">
							{showSearch && !isEmpty(searchList) && (
								<div className="search-list">
									{searchList.map((listItem: any, index: number) => {
										const isCategoryLevel = getIsCategoryLevel(listItem.categoryLevel);

										return (
											<NavLink
												to={`${eventUrl(listItem)}`}
												onClick={clickEventCard}
												key={`searchList-${index}`}
												data-cy="search-result"
											>
												<div
													className={`search__list-item search__list-item-desktop ${
														isCategoryLevel && 'search__list-category-item'
													}`}
												>
													<div className="flex justify__content--between align__items--center">
														<p
															className={`event--name ${
																listItem.area === 'LIVE_EVENT' ? 'live-event' : ''
															} flex align__items--center font--regular line-height--17 mt--2 mb--5 truncate--text ${
																isCategoryLevel && 'width--full'
															}`}
														>
															{listItem.area === 'LIVE_EVENT' && (
																<span className="event-status badge badge--red text--uppercase font--medium height--full mr--5">
																	live
																</span>
															)}
															<GetSportIcon id={listItem.extras.SPORT_ID} />
															{listItem.categoryLevel !== 1 && (
																<GetCategoriesIcon
																	category={{
																		sportId: listItem.extras.SPORT_ID,
																		parentCategory: listItem.extras.CATEGORY_ID_3,
																		categoryId: listItem.extras.CATEGORY_ID_2,
																	}}
																/>
															)}
															{!isCategoryLevel && (
																<span
																	className="line-height--17 truncate--text"
																	style={{ width: 'calc(100% - 60px)' }}
																>
																	{listItem.extras.CATEGORY_NAME_3}
																</span>
															)}
														</p>
														{!isCategoryLevel && (
															<p className="event--date no--margin font--regular line-height--15">
																{listItem.area === 'LIVE_EVENT'
																	? `${Math.round((new Date().getTime() - listItem.eventStart) / 1000 / 60)} min`
																	: moment(listItem.eventStart).local().format('DD.MM.YYYY HH:mm').toString()}
															</p>
														)}
													</div>
													<p
														className="mt--5 mb--0 search-title"
														dangerouslySetInnerHTML={{ __html: highlightSubstring(listItem.name, searchValue) }}
													/>
												</div>
											</NavLink>
										);
									})}
								</div>
							)}
							{showSearch && loading && searchValue && searchValue.length >= 3 && isEmpty(searchList) && (
								<div className="text--center font--regular font-size--12 m--10">
									<CircularProgress color="secondary" />
								</div>
							)}

							{showSearch && !loading && searchValue && searchValue.length >= 3 && isEmpty(searchList) && (
								<div className="text--center no-result-wrap">
									<NoEventsFound className={'no-result-search-icon'} />
									<p className="no-result-text" data-cy="no-result-text">
										{t('noSearchResults')}
									</p>
									<p>{t('noSearchResultTextTwo')}</p>
								</div>
							)}

							{showSearch && searchValue.length < 3 && (
								<div className="text--center no-result-wrap">
									<SearchIcon className={'no-result-search-icon'} />
									<p className="no-result-text">{t('searchEventInfo')}</p>
								</div>
							)}
						</div>
					</div>
				</Box>
			</Dialog>
		</div>
	);
};

export default SearcherDesktop;
